import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { LoaderSymbol } from '../../components/loading';
import { DEFAULT_DATE_RANGE } from '../../components/listDefaults';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useNavActions } from '../../hooks/useNavActions';
import { useAuthStore } from '../../auth/store';
import { fmtLocalDateTime, toWire } from '../../utils/datetime';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface PhCheckRow {
  oid:             string;
  jobId:           string;
  sampleId:        string;
  division:        string;
  containerId:     string;
  phCheck:         string | null;
  recordedPh:      string | null;   // FREE-TEXT — may carry a qualifier, e.g. "<26" (2026-10-01)
  adjustedPh:      string | null;
  dateTimeChecked: string | null;   // user-choosable on save (ISO, Central)
  checkedBy:       string | null;   // Employee Oid
  checkedByName:   string | null;   // resolved Employee name (display)
  statusName:      string;
  statusId:        string;
}

// A row's staged (unsaved) edits — the user-editable fields. Date/Time Checked is
// now user-choosable via the datetime picker (user request 2026-10-01); CheckedBy
// stays server-stamped (the acting employee).
type RowEdit = { phCheck?: string; recordedPh?: string; adjustedPh?: string; dateTimeChecked?: string; checkedBy?: string };

// 'All' or an exact lab Status value (e.g. "Pending Sampling", "Submitted",
// "Submitted, Subcontracted") — mirrors legacy's per-status column filter
// (Samplecheckin.Index.Status), NOT pH-recorded state.
type StatusFilter = string;
type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'All';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
// The date-range window as a MONTHS count. Sent to the API so the SP computes the
// cutoff from the DB SERVER date (GoLive parity: DateTime.Today.AddMonths(-N)),
// NOT the browser's date which drifts a calendar-day across timezones (that day
// drift dropped ~50 rows vs GoLive, 2026-08-12). 'All' → no filter.
const RANGE_MONTHS: Record<DateRangeKey, number | null> = {
  '1M': 1, '3M': 3, '6M': 6, '1Y': 12, '2Y': 24, '5Y': 60, 'All': null,
};

function hasPh(v: string | null): boolean {
  return !!v && v.trim().length > 0;
}

// The current (saved) value of an editable field, as the string shown in its cell.
function savedStr(row: PhCheckRow, field: keyof RowEdit): string {
  if (field === 'phCheck')         return row.phCheck ?? '';
  if (field === 'recordedPh')      return row.recordedPh ?? '';   // free text (may carry a qualifier)
  if (field === 'adjustedPh')      return row.adjustedPh ?? '';
  if (field === 'dateTimeChecked') return row.dateTimeChecked ?? '';   // saved WIRE string (datetime picker)
  return row.checkedByName ?? '';   // 'checkedBy' — saved Checked By NAME (combo value)
}

// Status → shared pill variant (backlog.css .bl-pill ok/warn/alert/info) — the
// ONLY coloured cell (BO UI Standard). Matches legacy's status families:
// Pending* = amber, plain Submitted = green, any compound "Submitted, X" (e.g.
// Subcontracted / Partially Subcontracted) = teal/info so it reads as a
// variant of Submitted rather than a wholly different state.
function statusPillVariant(statusName: string): string {
  const s = statusName.trim().toLowerCase();
  if (s.startsWith('pending'))          return 'warn';
  if (s === 'submitted')                return 'ok';
  if (s.startsWith('submitted,'))       return 'info';
  if (s.startsWith('cancel'))           return 'alert';
  return 'info';
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function SampleContainerPhCheckPage() {
  const [rows,      setRows]      = useState<PhCheckRow[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [loadMs,    setLoadMs]    = useState<number | null>(null);
  const [error,     setError]     = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeKey>(DEFAULT_DATE_RANGE.SampleTracking as DateRangeKey);
  const [statusFilt, setStatusFilt] = useState<StatusFilter>('All');   // lab Status (Pending Sampling / Submitted / …)
  const [search,    setSearch]    = useState('');
  const [selected,  setSelected]  = useState<Set<string | number>>(new Set());
  // Batch-edit buffer (legacy InlineEditMode="Batch"): pH edits accumulate here,
  // keyed by row Oid, and are NOT sent to the server until "Save" is clicked —
  // "Cancel" discards them. Matches the Navigation Manager pattern.
  const [edits,     setEdits]     = useState<Record<string, RowEdit>>({});
  const [saving,    setSaving]    = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const notify    = useNotify();
  const runSave   = useSaveFlow();
  // Module Access Control (Role-Based MAC) — Write gates recording a pH value.
  // Falls open (true) for admins / no MAC data, same as every other MAC-gated
  // form in this app; only tightens where a lab has configured a MAC level.
  const canWrite  = useNavActions('sampphcheck').write;
  // Current login's display name — the default "Checked By" when a row is checked.
  const currentUser = useAuthStore(s => s.user?.name ?? 'Service');
  // User roster for the "Checked By" dropdown (login DisplayName + Oid) — the SAME
  // source as Field Data Entry's EnteredBy combo. The combo shows the name; the save
  // resolves the chosen name back to the Employee Oid written to CheckedBy.
  const [employees, setEmployees] = useState<{ oid: string; name: string }[]>([]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      const months = RANGE_MONTHS[dateRange];
      if (months != null) params.months = String(months);
      const res = await api.get('/api/v1/sample-container-ph-check', { params });
      setRows((res.data as { data: PhCheckRow[] }).data ?? []);
      setEdits({});
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { void load(); }, [load]);

  // User roster for the "Checked By" combo (login DisplayName + Oid).
  useEffect(() => {
    api.get('/api/v1/lookups/employees', { silentLoading: true } as never)
      .then(r => setEmployees(((r.data as { data: { oid: string; displayName: string; userName: string }[] }).data ?? [])
        .map(e => ({ oid: e.oid, name: e.displayName || e.userName }))))
      .catch(() => { /* the combo just shows no options if the roster fails to load */ });
  }, []);

  // ── Inline edit — click-to-edit stages the value in the local batch buffer
  // (legacy InlineEditMode="Batch"); nothing hits the server until Save. Handles
  // all three editable fields (pHCheck / Recorded pH / Adjusted pH) per row. ──
  const stageEdit = useCallback((row: PhCheckRow, field: keyof RowEdit, newValue: string) => {
    const trimmed = newValue.trim();
    setEdits(prev => {
      const cur: RowEdit = { ...(prev[row.oid] ?? {}) };
      cur[field] = trimmed;
      // Row is dirty only if some staged field differs from its saved value.
      const dirty = (['phCheck', 'recordedPh', 'adjustedPh', 'dateTimeChecked', 'checkedBy'] as (keyof RowEdit)[])
        .some(f => (cur[f] ?? savedStr(row, f)) !== savedStr(row, f));
      const next = { ...prev };
      if (dirty) next[row.oid] = cur; else delete next[row.oid];
      return next;
    });
  }, []);

  const dirtyCount = Object.keys(edits).length;

  // Selecting a container MARKS it checked NOW (user request 2026-10-01): ticking a
  // row whose Date/Time Checked is empty stamps it with the current time (and
  // Checked By previews the current user — the server stamps CheckedBy on save), so
  // "tick the ones you checked → Save" needs no manual date pick. Unticking a row WE
  // auto-stamped reverts it; a row whose date was picked by hand is left alone
  // (tracked in autoCheckedRef, cleared when the cell is edited manually).
  const autoCheckedRef = useRef<Set<string>>(new Set());
  const handleSelectionChange = useCallback((next: Set<string | number>) => {
    if (canWrite) {
      const nowWire = toWire(new Date());
      for (const id of next) {                       // newly selected → stamp when empty
        if (selected.has(id)) continue;
        const row = rows.find(r => r.oid === String(id));
        if (!row) continue;
        const hasDtc = (edits[row.oid]?.dateTimeChecked ?? row.dateTimeChecked ?? '') !== '';
        if (hasDtc) continue;
        autoCheckedRef.current.add(row.oid);
        stageEdit(row, 'dateTimeChecked', nowWire);
        // Default Checked By to the current user (the dropdown can be changed after).
        if ((edits[row.oid]?.checkedBy ?? row.checkedByName ?? '') === '')
          stageEdit(row, 'checkedBy', currentUser);
      }
      for (const id of selected) {                   // newly deselected → revert ours
        if (next.has(id)) continue;
        const oid = String(id);
        if (!autoCheckedRef.current.has(oid)) continue;
        autoCheckedRef.current.delete(oid);
        const row = rows.find(r => r.oid === oid);
        if (row) {
          stageEdit(row, 'dateTimeChecked', savedStr(row, 'dateTimeChecked'));
          stageEdit(row, 'checkedBy', savedStr(row, 'checkedBy'));
        }
      }
    }
    setSelected(next);
  }, [canWrite, rows, selected, edits, stageEdit, currentUser]);

  // "Cancel changes" — discard every staged edit, back to last-loaded values,
  // and clear row selection too (checking a row while editing shouldn't leave
  // it looking "armed" once the edit itself has been discarded).
  const cancelEdits = useCallback(() => {
    setEdits({});
    setSelected(new Set());
    autoCheckedRef.current.clear();
  }, []);

  // "Save changes" — the shared save flow (confirm → audited PUT per row →
  // "Saved successfully.") — same pattern as Navigation Manager's batch save.
  const save = useCallback(() => {
    if (dirtyCount === 0) { notify.info('No changes to save.'); return; }
    setSaving(true);
    runSave({
      formName: 'Sample Container pH Check',
      auditAction: 'edit',
      save: async () => {
        for (const [oid, e] of Object.entries(edits)) {
          const row = rows.find(r => r.oid === oid);
          // Send the row's EFFECTIVE values (staged edit, else current) for all
          // editable fields — the server sets each column, so null = clear.
          const effPh  = (e.phCheck    ?? (row ? savedStr(row, 'phCheck')    : '')).trim();
          // Recorded/Adjusted pH are FREE TEXT — send the raw string so a qualifier
          // like "<26" survives (no numeric parse). (user request 2026-10-01)
          const effRec = (e.recordedPh ?? (row ? savedStr(row, 'recordedPh') : '')).trim();
          const effAdj = (e.adjustedPh ?? (row ? savedStr(row, 'adjustedPh') : '')).trim();
          // Date/Time Checked — the EFFECTIVE value (newly-picked wire string, else
          // the saved one). null when never set → the server defaults it to the save
          // time (ISNULL(@DateTimeChecked, GETDATE())), preserving legacy behaviour.
          const effDtc = (e.dateTimeChecked ?? (row ? savedStr(row, 'dateTimeChecked') : '')).trim();
          // Checked By — resolve the chosen name back to its Employee Oid (null → the
          // server defaults it to the acting employee, preserving legacy behaviour).
          const effChk = (e.checkedBy ?? (row ? savedStr(row, 'checkedBy') : '')).trim();
          const checkedByOid = employees.find(em => em.name === effChk)?.oid ?? null;
          await api.put(`/api/v1/sample-container-ph-check/${oid}`, {
            phCheck:         effPh || null,
            recordedPh:      effRec || null,
            adjustedPh:      effAdj || null,
            dateTimeChecked: effDtc || null,
            checkedByOid,
          });
        }
        await load();
        setSelected(new Set());
        autoCheckedRef.current.clear();
      },
      successMessage: `Updated ${dirtyCount} container(s).`,
    }).finally(() => setSaving(false));
  }, [dirtyCount, edits, rows, employees, notify, runSave, load]);

  // ── Derived data ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let r = rows;
    if (statusFilt !== 'All') r = r.filter(x => x.statusName === statusFilt);
    if (search.trim()) {
      const t = search.toLowerCase();
      r = r.filter(x =>
        x.jobId.toLowerCase().includes(t) ||
        x.sampleId.toLowerCase().includes(t) ||
        x.containerId.toLowerCase().includes(t) ||
        x.statusName.toLowerCase().includes(t)
      );
    }
    return r;
  }, [rows, statusFilt, search]);

  // The grid's OWN column-filtered + sorted set (pre-pagination) — kept in sync
  // via onVisibleRowsChange so "Export to" exports exactly what's on screen,
  // including any active column filter (e.g. Sample ID), not just the
  // page-level search/status-filtered rows.
  const [visibleRows, setVisibleRows] = useState<PhCheckRow[]>(filtered);

  // Distinct lab Status values present in the data, with counts — mirrors
  // legacy's per-status filter breakdown (e.g. Pending Sampling / Submitted /
  // Submitted, Subcontracted) instead of a fixed, hardcoded set, so any
  // status family that appears just shows up as its own chip.
  const statusBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(r.statusName, (counts.get(r.statusName) ?? 0) + 1);
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const checkedCount = useMemo(() => rows.filter(x => hasPh(x.phCheck)).length, [rows]);
  const pendingCount = useMemo(() => rows.length - checkedCount, [rows, checkedCount]);
  const checkedPct   = rows.length ? Math.round(checkedCount / rows.length * 100) : 0;

  // Shared-grid columns — pH is the ONE inline-editable cell (click-to-edit,
  // shared <InlineCellEditor>); everything else is read-only display. The pH
  // `value` accessor reflects any STAGED (unsaved) edit so the cell shows what
  // you typed even before Save is clicked.
  const cols: AppColumn<PhCheckRow>[] = [
    { field: 'jobId',       header: 'Job ID',       width: 100 },
    { field: 'sampleId',    header: 'Sample ID',    width: 130 },
    { field: 'containerId', header: 'Container ID', width: 110 },
    // pHCheck column removed from the grid (user request 2026-10-01). The column's
    // data is still fetched and preserved on save (phCheck sent as its saved value),
    // but it is no longer shown or editable here — only Recorded pH / Adjusted pH are.
    { field: 'recordedPh',  header: 'Recorded pH', width: 100,
      value: r => edits[r.oid]?.recordedPh ?? savedStr(r, 'recordedPh'),
      text:  r => edits[r.oid]?.recordedPh ?? savedStr(r, 'recordedPh'),
      // Plain text box — NO number spinner (user request 2026-10-01); the save
      // parses the text to a decimal (toNum), DB column stays DECIMAL(5,2).
      edit: { type: 'text', trigger: 'click', disabled: () => !canWrite } },
    { field: 'adjustedPh',  header: 'Adjusted pH', width: 100,
      value: r => edits[r.oid]?.adjustedPh ?? savedStr(r, 'adjustedPh'),
      text:  r => edits[r.oid]?.adjustedPh ?? savedStr(r, 'adjustedPh'),
      edit: { type: 'text', trigger: 'click', disabled: () => !canWrite } },
    { field: 'dateTimeChecked', header: 'Date/Time Checked', width: 160,
      // Editable via the shared datetime-local PICKER (user request 2026-10-01) —
      // click the cell → calendar + time. `value` is the WIRE string the
      // <InlineCellEditor> datetime mode reads; text/render show it formatted
      // (MM/DD/YYYY HH:mm, 24h — §14b.6). Reflects any STAGED (unsaved) pick.
      value: r => edits[r.oid]?.dateTimeChecked ?? (r.dateTimeChecked ?? ''),
      text:  r => { const v = edits[r.oid]?.dateTimeChecked ?? r.dateTimeChecked; return v ? fmtLocalDateTime(v) : ''; },
      render: r => { const v = edits[r.oid]?.dateTimeChecked ?? r.dateTimeChecked; return <>{v ? fmtLocalDateTime(v) : ''}</>; },
      edit: { type: 'datetime', trigger: 'click', disabled: () => !canWrite } },
    // NOTE: field is 'checkedBy' (the RowEdit key), NOT 'checkedByName' — so the
    // combo's onCellCommit/onCellChange stage under the same key the save reads; with
    // field 'checkedByName' the pick never matched the stage condition and reverted to
    // the current user (user report 2026-10-01). value/text show the name regardless.
    { field: 'checkedBy', header: 'Checked By', width: 150,
      // Editable DROPDOWN of users (user request 2026-10-01) — same roster as Field
      // Data Entry's EnteredBy. Shows the staged pick, else the saved name; the save
      // resolves the chosen name back to the Employee Oid written to CheckedBy.
      // Selecting a row auto-fills this with the current user (handleSelectionChange).
      value: r => edits[r.oid]?.checkedBy ?? (r.checkedByName ?? ''),
      text:  r => edits[r.oid]?.checkedBy ?? (r.checkedByName ?? ''),
      edit: { type: 'combo', options: () => employees.map(e => e.name), disabled: () => !canWrite } },
    { field: 'statusName',  header: 'Status', width: 180,
      text: r => r.statusName,
      render: r => <span className={`bl-pill ${statusPillVariant(r.statusName)}`}>{r.statusName}</span> },
  ];

  // Phase 2.3 — Sample Container pH Check keys (CLAUDE.md standing rule 14, §18.6,
  // ADR-0013): Ctrl+S Save · Esc Cancel — mirror the batch-edit Save/Cancel pair
  // (same handlers + disabled gate).  Only registered for MAC-Write users (the
  // buttons are hidden otherwise).  Esc is allowInInput:false so it never discards
  // staged edits while a pH cell editor is focused (the cell keeps its own Esc).
  useHotkeys(canWrite ? [
    { combo: 'mod+s', label: 'Save', group: 'Sample Container pH Check', order: 1,
      run: () => save(), disabled: saving || dirtyCount === 0 },
    { combo: 'esc', label: 'Cancel', group: 'Sample Container pH Check', order: 9, allowInInput: false,
      run: () => cancelEdits(), disabled: saving || dirtyCount === 0 },
  ] : []);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="bl-page">

        {/* ── Fixed top: breadcrumb + title + KPIs ──────────────────────── */}
        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span>
              <i className="ti ti-chevron-right" />
              <span>Sample Tracking</span>
              <i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Sample Container pH Check</span>
            </div>
            {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
                via navMeta + the TOP load-time chip. */}
            <div className="dp-title-row">
              <FormTitle pageId="sampphcheck" auditFormName="Sample Container pH Check" loadMs={loadMs} loading={loading} />
              {/* Save / Cancel — batch-edit pattern (same as Navigation Manager):
                  disabled until a pH cell is edited. Sits top-right of the title
                  row, not the toolbar, so it stays visible while scrolling.
                  Hidden entirely (not just disabled) when Module Access Control
                  doesn't grant Write — same "hide, not disable" convention every
                  other MAC-gated button in this app follows. */}
              {canWrite && (
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button className="ca-btn ca-btn-solid" onClick={save} disabled={saving || dirtyCount === 0} title={`Save changes (${formatCombo('mod+s')})`}>
                    {saving ? <><LoaderSymbol size={13} /> Saving…</> : <><span className="tb-i">💾</span> Save</>}
                  </button>
                  <button className="ca-btn" onClick={cancelEdits} disabled={saving || dirtyCount === 0} title={`Cancel changes (${formatCombo('esc')})`}>
                    <span className="tb-i">✕</span> Cancel
                  </button>
                </div>
              )}
            </div>
            <div className="dp-subtitle">
              {canWrite
                ? <>Click a pH cell to record a reading · {loading ? '…' : rows.length} items · {dateRange}</>
                : <>Read-only — you don't have Write permission for Sample Container pH Check · {loading ? '…' : rows.length} items · {dateRange}</>}
            </div>
          </div>

          {/* KPI row */}
          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-lbl">Total ({dateRange})</div>
              <div className="kpi-val kpi-blue">{loading ? '…' : rows.length}</div>
              <div className="kpi-chg">Items in range</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">pH Recorded</div>
              <div className="kpi-val kpi-teal">{loading ? '…' : checkedCount}</div>
              <div className="kpi-chg" style={{ color: 'var(--ai-green)' }}>{checkedPct}%</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">pH Pending</div>
              <div className="kpi-val kpi-red">{loading ? '…' : pendingCount}</div>
              <div className="kpi-chg" style={{ color: 'var(--ai-amber)' }}>{100 - checkedPct}%</div>
            </div>
          </div>
        </div>

        {/* ── Grid area: toolbar + search sit ON the grey page (BO UI Standard
            v2); only the grid card below is white. ─────────────────────── */}
        <div className="bl-grid-area">
          {/* Toolbar — canonical order: [left] · Refresh · Export to. No
              New/Edit/Delete — the pH cell itself is the edit surface. */}
          <div style={{ margin: '0 0 8px' }}>
            <AppToolbar
              left={
                <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                  value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                  onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
              }
              onRefresh={load}
              refreshing={loading}
              exportData={{ filename: 'sample-container-ph-check', ...gridExportMatrix(visibleRows, cols) }}
            />
          </div>

          {/* Global search — shared .list-search-row on the grey page, never inside the card. */}
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input
                ref={searchRef}
                type="text"
                placeholder="Search job ID, sample ID, container, status…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <span className="list-search-clear" onClick={() => setSearch('')}>×</span>
              )}
            </div>
            {(filtered.length !== rows.length) && (
              <span className="search-match-count">
                {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
              </span>
            )}
            {dirtyCount > 0 && (
              <span className="search-match-count"><b style={{ color: '#b5560c' }}>{dirtyCount} unsaved</b></span>
            )}
          </div>

          {/* ONE white grid card — filter-chip strip + grid + its single footer. */}
          <div className="bl-card bl-card-flex">

            {/* Status filter — shared .sa-filter-* chips (backlog.css). Chips are
                the DISTINCT lab Status values present (Pending Sampling /
                Submitted / Submitted, Subcontracted / …), matching legacy's
                own per-status breakdown — not pH-recorded state (see the pH
                Recorded / pH Pending KPI tiles above for that). */}
            <div className="sa-filter-row">
              <span className="sa-filter-lbl">Status:</span>
              <button
                className={`sa-filter-btn${statusFilt === 'All' ? ' active' : ''}`}
                onClick={() => setStatusFilt('All')}
              >
                All ({rows.length})
              </button>
              {statusBreakdown.map(([name, count]) => (
                <button
                  key={name}
                  className={`sa-filter-btn${statusFilt === name ? ' active' : ''}`}
                  onClick={() => setStatusFilt(name)}
                >
                  {name} ({count})
                </button>
              ))}
            </div>

            {/* Table — load failure stays inline; AppDataGrid handles the rest */}
            {error ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {error}
                <button onClick={load} style={{ marginLeft: 10, color: 'var(--brand-accent, #e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
              </div>
            ) : (
              /* Shared data grid — styling, per-column filters, sort, footer /
                 pagination, load-time and inline editing are all standardised
                 in <AppDataGrid>. */
              <AppDataGrid<PhCheckRow>
                rows={filtered}
                columns={cols}
                getRowId={r => r.oid}
                loading={loading}
                loadMs={loadMs}
                selectable
                selectedIds={selected}
                onSelectionChange={handleSelectionChange}
                onVisibleRowsChange={setVisibleRows}
                onCellCommit={(row, column, newValue) => {
                  if (column.field === 'phCheck' || column.field === 'recordedPh' || column.field === 'adjustedPh' || column.field === 'dateTimeChecked' || column.field === 'checkedBy') {
                    if (column.field === 'dateTimeChecked' || column.field === 'checkedBy') autoCheckedRef.current.delete(row.oid);  // manual pick → not auto-populated
                    stageEdit(row, column.field as keyof RowEdit, newValue);
                  }
                }}
                // Stage on every keystroke too (not just blur/Enter) — Save
                // enables the moment you type, no need to click away first.
                onCellChange={(row, column, newValue) => {
                  if (column.field === 'phCheck' || column.field === 'recordedPh' || column.field === 'adjustedPh' || column.field === 'dateTimeChecked' || column.field === 'checkedBy') {
                    if (column.field === 'dateTimeChecked' || column.field === 'checkedBy') autoCheckedRef.current.delete(row.oid);  // manual pick → not auto-populated
                    stageEdit(row, column.field as keyof RowEdit, newValue);
                  }
                }}
                emptyText="No records found"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
